import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import toast from 'react-hot-toast';
const AuthContext = createContext(undefined);
const TEICO_AUTH_STORAGE_KEYS = [
    'teico_token',
    'teico_user',
    'teico_tenant_id',
    'teico_refresh_token',
    'teico_id_token',
    'teico_oidc_session',
];
const clearStoredAuthCredentials = () => {
    TEICO_AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [tenantId, setTenantId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isReady, setIsReady] = useState(false); // API準備完了フラグ
    // OAuth認証成功データ処理
    const handleOAuthSuccess = useCallback(async (sessionKey) => {
        try {
            console.log('🔄 [AUTH] Processing OAuth authentication success');
            const response = await fetch(`/auth/oauth/session/${sessionKey}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'User-Agent': 'TEICO-Fuchi/1.0'
                }
            });
            if (!response.ok) {
                throw new Error(`OAuth session retrieval failed: ${response.status}`);
            }
            const result = await response.json();
            if (result.success && result.data) {
                const { user: userData, token: accessToken, refresh_token: refreshToken, id_token: idToken, oidc_session: oidcSession } = result.data;
                // ユーザーデータの存在確認
                if (!userData) {
                    console.error('User data missing in OAuth response:', result.data);
                    return { success: false, error: 'ユーザーデータの取得に失敗しました' };
                }
                // OIDC Token検証 (Phase 19.1-A3)
                if (oidcSession) {
                    console.log('✅ [AUTH-OAUTH] OIDC session received:', {
                        user_id: oidcSession.user_info?.sub,
                        username: oidcSession.user_info?.preferred_username,
                        tenant_id: oidcSession.tenant_id,
                        expires_at: new Date(oidcSession.expires_at).toISOString()
                    });
                }
                // 認証状態を設定
                // テナントIDの検証（'default'は無効）
                const oidcTenantId = oidcSession?.tenant_id && oidcSession.tenant_id !== 'default' ? oidcSession.tenant_id : null;
                const userTenantId = userData.tenant_id && userData.tenant_id !== 'default' ? userData.tenant_id : null;
                const effectiveTenantId = oidcTenantId || userTenantId;
                // デバッグ: テナントIDの取得状況をログ出力
                console.log('🔍 [AUTH-OAUTH] Tenant ID extraction:', {
                    oidcSession_tenant_id: oidcSession?.tenant_id,
                    userData_tenant_id: userData.tenant_id,
                    oidcTenantId,
                    userTenantId,
                    effectiveTenantId,
                    userData: {
                        id: userData.id,
                        username: userData.username,
                        email: userData.email
                    }
                });
                setUser(userData);
                setToken(accessToken);
                setTenantId(effectiveTenantId);
                apiClient.setToken(accessToken);
                // localStorage保存処理
                localStorage.setItem('teico_token', accessToken);
                localStorage.setItem('teico_user', JSON.stringify(userData));
                if (refreshToken) {
                    localStorage.setItem('teico_refresh_token', refreshToken);
                }
                if (idToken) {
                    localStorage.setItem('teico_id_token', idToken);
                }
                if (oidcSession) {
                    localStorage.setItem('teico_oidc_session', JSON.stringify(oidcSession));
                }
                if (effectiveTenantId) {
                    localStorage.setItem('teico_tenant_id', effectiveTenantId);
                }
                else {
                    // 'default'が保存されていた場合は削除
                    localStorage.removeItem('teico_tenant_id');
                }
                console.log('✅ [AUTH] OAuth authentication completed successfully');
                // 🔧 変更: e3smart認証はオンデマンドで実行（エージェント作成時に必要になったら認証）
                // checkE3SmartAuth(userData.id, userData.tenant_id || 'default');
                return { success: true };
            }
            else {
                console.error('OAuth session retrieval failed:', result.error);
                return { success: false, error: result.error || 'OAuth認証に失敗しました' };
            }
        }
        catch (error) {
            console.error('OAuth authentication error:', error);
            return { success: false, error: 'OAuth認証中にエラーが発生しました' };
        }
    }, []);
    // e3smartコールバック処理
    const handleE3SmartCallback = useCallback(async (sessionKey) => {
        try {
            console.log('[E3SMART-AUTH] Retrieving e3smart token from session');
            const response = await fetch(`/api/mizu/dive/oauth/e3smart/token?session=${sessionKey}`);
            const result = await response.json();
            if (result.status === 'success' && result.data) {
                console.log('[E3SMART-AUTH] e3smart authentication completed successfully');
                console.log('[E3SMART-AUTH] Token stored in Redis, will be retrieved on-demand');
                // 🔧 既存のlocalStorageトークンを削除（古いトークンを使わないように）
                localStorage.removeItem('e3smart_token');
                localStorage.removeItem('e3smart_refresh_token');
                // 🔧 ポップアップウィンドウの場合は親ウィンドウに通知して閉じる
                if (window.opener && !window.opener.closed) {
                    console.log('[E3SMART-AUTH] Running in popup window, notifying parent and closing');
                    // 親ウィンドウにメッセージを送信
                    window.opener.postMessage({ type: 'e3smart_auth_success' }, window.location.origin);
                    // ポップアップを閉じる
                    setTimeout(() => {
                        window.close();
                    }, 500);
                    return;
                }
                // URLパラメータをクリア（メインウィンドウの場合のみ）
                window.history.replaceState({}, document.title, '/');
            }
        }
        catch (error) {
            console.error('[E3SMART-AUTH] Error handling e3smart callback:', error);
            // 🔧 ポップアップウィンドウでエラーが発生した場合も閉じる
            if (window.opener && !window.opener.closed) {
                console.error('[E3SMART-AUTH] Error in popup window, notifying parent and closing');
                window.opener.postMessage({ type: 'e3smart_auth_error', error: String(error) }, window.location.origin);
                setTimeout(() => {
                    window.close();
                }, 100);
            }
        }
    }, []);
    // e3smart認証状態をチェックし、未認証の場合は認証フローを開始
    const checkE3SmartAuth = useCallback(async (userId, tenantId) => {
        try {
            console.log('[E3SMART-AUTH] Checking e3smart authentication status', { userId, tenantId });
            // e3smartトークン状態を確認（Redisをチェック）
            const response = await fetch(`/api/mizu/dive/oauth/e3smart/status?user_id=${userId}&tenant_id=${tenantId}`);
            if (!response.ok) {
                console.error('[E3SMART-AUTH] Failed to check token status:', response.status, response.statusText);
                return;
            }
            const result = await response.json();
            console.log('[E3SMART-AUTH] Token status check result:', result);
            if (result.status === 'success' && result.has_token) {
                console.log('[E3SMART-AUTH] e3smart token already exists in Redis');
                // 🔧 既存のlocalStorageトークンを削除（Redisが優先）
                localStorage.removeItem('e3smart_token');
                localStorage.removeItem('e3smart_refresh_token');
                return;
            }
            console.log('[E3SMART-AUTH] No e3smart token in Redis, starting OAuth flow');
            // 🔧 既存のlocalStorageトークンを削除（古いトークンを使わないように）
            localStorage.removeItem('e3smart_token');
            localStorage.removeItem('e3smart_refresh_token');
            // 認証URL取得
            console.log('[E3SMART-AUTH] Requesting auth URL...');
            const authResponse = await fetch(`/api/mizu/dive/oauth/e3smart/auth?user_id=${userId}&tenant_id=${tenantId}`);
            if (!authResponse.ok) {
                console.error('[E3SMART-AUTH] Failed to get auth URL:', authResponse.status, authResponse.statusText);
                const errorText = await authResponse.text();
                console.error('[E3SMART-AUTH] Error response:', errorText);
                // E3SMART_CLIENT_IDが設定されていない場合は認証フローをスキップ
                try {
                    const errorResult = JSON.parse(errorText);
                    if (errorResult.message && errorResult.message.includes('E3SMART_CLIENT_ID is not configured')) {
                        console.warn('[E3SMART-AUTH] E3SMART_CLIENT_ID is not configured. Skipping e3smart authentication.');
                        console.warn('[E3SMART-AUTH] e3smart tools will not be available until E3SMART_CLIENT_ID is configured.');
                        return;
                    }
                }
                catch (parseError) {
                    // JSON解析に失敗した場合は通常のエラーとして扱う
                    console.error('[E3SMART-AUTH] Failed to parse error response:', parseError);
                }
                return;
            }
            const authResult = await authResponse.json();
            console.log('[E3SMART-AUTH] Auth URL response:', authResult);
            if (authResult.status === 'success' && authResult.auth_url) {
                console.log('[E3SMART-AUTH] Opening authentication popup:', authResult.auth_url);
                // 認証URLをポップアップで開く
                const width = 600;
                const height = 700;
                const left = window.screen.width / 2 - width / 2;
                const top = window.screen.height / 2 - height / 2;
                const authWindow = window.open(authResult.auth_url, 'e3smart_auth', `width=${width},height=${height},left=${left},top=${top}`);
                if (!authWindow) {
                    console.error('[E3SMART-AUTH] Popup blocked by browser. Please allow popups for this site.');
                    toast.error('e3smart認証のためにポップアップを許可してください。');
                    return;
                }
                console.log('[E3SMART-AUTH] Popup opened successfully');
                // ポップアップの完了を監視
                const checkClosed = setInterval(() => {
                    if (authWindow && authWindow.closed) {
                        clearInterval(checkClosed);
                        console.log('[E3SMART-AUTH] Authentication window closed');
                        // URLパラメータから認証結果を確認
                        const params = new URLSearchParams(window.location.search);
                        const e3smartSuccess = params.get('e3smart_success');
                        const sessionKey = params.get('session');
                        if (e3smartSuccess === 'true' && sessionKey) {
                            handleE3SmartCallback(sessionKey);
                        }
                    }
                }, 500);
            }
            else {
                console.error('[E3SMART-AUTH] Failed to get auth URL from response:', authResult);
            }
        }
        catch (error) {
            console.error('[E3SMART-AUTH] Error checking e3smart auth:', error);
        }
    }, [handleE3SmartCallback]);
    // Token自動リフレッシュ機構（19.2-A3）
    const refreshToken = useCallback(async () => {
        try {
            console.log('🔄 [AUTH-REFRESH] Starting token refresh');
            const savedRefreshToken = localStorage.getItem('teico_refresh_token');
            if (!savedRefreshToken) {
                console.error('❌ [AUTH-REFRESH] No refresh token available');
                return { success: false, error: 'リフレッシュトークンが見つかりません' };
            }
            const response = await fetch('/auth/oauth/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    refresh_token: savedRefreshToken
                })
            });
            if (!response.ok) {
                throw new Error(`Token refresh failed: ${response.status}`);
            }
            const result = await response.json();
            if (result.success && result.data) {
                const { user: userData, token: accessToken, refresh_token: newRefreshToken, id_token: idToken, oidc_session: oidcSession } = result.data;
                if (!userData || !accessToken) {
                    console.error('❌ [AUTH-REFRESH] Refresh response missing user or access token:', result.data);
                    return { success: false, error: 'リフレッシュレスポンスが不正です' };
                }
                const effectiveTenantId = userData.tenant_id && userData.tenant_id !== 'default' ? userData.tenant_id : null;
                // 認証状態を更新
                setUser(userData);
                setToken(accessToken);
                setTenantId(effectiveTenantId);
                apiClient.setToken(accessToken);
                // localStorage を更新
                localStorage.setItem('teico_token', accessToken);
                localStorage.setItem('teico_user', JSON.stringify(userData));
                if (effectiveTenantId) {
                    localStorage.setItem('teico_tenant_id', effectiveTenantId);
                }
                else {
                    localStorage.removeItem('teico_tenant_id');
                }
                if (newRefreshToken) {
                    localStorage.setItem('teico_refresh_token', newRefreshToken);
                }
                if (idToken) {
                    localStorage.setItem('teico_id_token', idToken);
                }
                if (oidcSession) {
                    localStorage.setItem('teico_oidc_session', JSON.stringify(oidcSession));
                }
                console.log('✅ [AUTH-REFRESH] Token refresh successful');
                return { success: true };
            }
            else {
                console.error('❌ [AUTH-REFRESH] Token refresh failed:', result.error);
                return { success: false, error: result.error || 'トークンリフレッシュに失敗しました' };
            }
        }
        catch (error) {
            console.error('❌ [AUTH-REFRESH] Token refresh error:', error);
            return { success: false, error: 'トークンリフレッシュ中にエラーが発生しました' };
        }
    }, []);
    useEffect(() => {
        apiClient.setAuthRefreshHandler(async () => {
            const result = await refreshToken();
            return result.success;
        });
        return () => {
            apiClient.setAuthRefreshHandler(null);
        };
    }, [refreshToken]);
    useEffect(() => {
        // 🔧 e3smart認証成功パラメータのチェック（ポップアップウィンドウ用）
        // このチェックは user/token チェックより前に実行する必要がある
        const urlParams = new URLSearchParams(window.location.search);
        const e3smartSuccess = urlParams.get('e3smart_success');
        const e3smartSession = urlParams.get('session');
        console.log('[E3SMART-AUTH-DEBUG] useEffect triggered');
        console.log('[E3SMART-AUTH-DEBUG] e3smartSuccess:', e3smartSuccess, 'session:', e3smartSession);
        if (e3smartSuccess === 'true' && e3smartSession) {
            console.log('[E3SMART-AUTH] Detected e3smart auth callback in URL');
            handleE3SmartCallback(e3smartSession);
            return;
        }
        // 🔧 Google MCP Server OAuth認証コールバック処理（ポップアップウィンドウ用）
        // Mizuバックエンドが /?mcp_auth_success=<serverName> または /?mcp_error=<error> にリダイレクトする
        const mcpAuthSuccess = urlParams.get('mcp_auth_success');
        const mcpError = urlParams.get('mcp_error');
        if (mcpAuthSuccess) {
            console.log('[GOOGLE-MCP-AUTH] Detected MCP auth success in URL:', mcpAuthSuccess);
            window.history.replaceState({}, '', window.location.pathname);
            if (window.opener && !window.opener.closed) {
                console.log('[GOOGLE-MCP-AUTH] Running in popup window, notifying parent and closing');
                window.opener.postMessage({ type: 'mcp_auth_success', server: mcpAuthSuccess }, window.location.origin);
                setTimeout(() => {
                    window.close();
                }, 500);
            }
            return;
        }
        if (mcpError) {
            console.error('[GOOGLE-MCP-AUTH] MCP auth error in URL:', mcpError);
            window.history.replaceState({}, '', window.location.pathname);
            if (window.opener && !window.opener.closed) {
                console.error('[GOOGLE-MCP-AUTH] Error in popup window, notifying parent and closing');
                window.opener.postMessage({ type: 'mcp_error', error: decodeURIComponent(mcpError) }, window.location.origin);
                setTimeout(() => {
                    window.close();
                }, 100);
            }
            return;
        }
        // 重複初期化防止チェック
        if (user && token) {
            setIsLoading(false);
            return;
        }
        // OAuth認証成功パラメータのチェック
        const oauthSuccess = urlParams.get('oauth_success');
        const sessionKey = urlParams.get('session');
        const oauthError = urlParams.get('error');
        if (oauthSuccess === 'true' && sessionKey) {
            console.log('🔄 [AUTH] OAuth authentication success detected');
            // URLパラメータをクリア（エラー表示前に実行）
            window.history.replaceState({}, document.title, '/');
            // OAuth認証成功処理（19.2-A2: 認可コールバック処理改良）
            handleOAuthSuccess(sessionKey).then((result) => {
                if (result.success) {
                    console.log('✅ [AUTH] OAuth initialization completed successfully');
                    // 成功時の追加処理
                    const userData = JSON.parse(localStorage.getItem('teico_user') || '{}');
                    console.log('👤 [AUTH] OAuth user authenticated:', {
                        id: userData.id,
                        username: userData.username,
                        tenant_id: userData.tenant_id,
                        role: userData.role
                    });
                    // 自動リフレッシュタイマー開始のための再レンダリング
                    setIsReady(true);
                }
                else {
                    console.error('❌ [AUTH] OAuth initialization failed:', result.error);
                    // OAuth認証失敗時のエラーハンドリング改良
                    const errorMessage = result.error || 'OAuth認証に失敗しました';
                    // HomePageにエラー情報を渡すためのフラグ設定
                    sessionStorage.setItem('oauth_error_message', errorMessage);
                    // エラー時もホームページに移動（エラー表示のため）
                    window.location.href = '/?oauth_error=true';
                }
                setIsLoading(false);
            }).catch((error) => {
                console.error('❌ [AUTH] OAuth initialization error:', error);
                // 予期しないエラーのハンドリング
                const errorMessage = 'OAuth認証処理中に予期しないエラーが発生しました';
                sessionStorage.setItem('oauth_error_message', errorMessage);
                setIsLoading(false);
                window.location.href = '/?oauth_error=true';
            });
            return; // 通常の初期化をスキップ
        }
        if (oauthError) {
            console.error('❌ [AUTH] OAuth error detected:', oauthError);
            // URLパラメータをクリア
            window.history.replaceState({}, document.title, '/');
            setIsLoading(false);
            return;
        }
        const savedToken = localStorage.getItem('teico_token');
        const savedUser = localStorage.getItem('teico_user');
        const savedTenantId = localStorage.getItem('teico_tenant_id');
        const savedRefreshToken = localStorage.getItem('teico_refresh_token');
        const savedIdToken = localStorage.getItem('teico_id_token');
        const savedOidcSession = localStorage.getItem('teico_oidc_session');
        // 'default'テナントIDは無効として扱う（テナント分離のため）
        const validSavedTenantId = savedTenantId && savedTenantId !== 'default' ? savedTenantId : null;
        const tryRefreshExpiredCredentials = (reason) => {
            if (!savedRefreshToken) {
                return false;
            }
            console.warn(`⚠️ [AUTH-INIT] ${reason} - attempting refresh before logout`);
            refreshToken()
                .then((result) => {
                if (!result.success) {
                    console.error('❌ [AUTH-INIT] Stored token refresh failed:', result.error);
                    clearStoredAuthCredentials();
                }
            })
                .finally(() => {
                setIsLoading(false);
            });
            return true;
        };
        let willAuthenticate = false;
        if (savedToken && savedUser) {
            // OIDC Session情報がある場合は優先使用 (Phase 19.1-A3)
            try {
                if (savedOidcSession) {
                    console.log('🔍 [AUTH-INIT] OIDC session found in localStorage');
                    const oidcSession = JSON.parse(savedOidcSession);
                    // OIDC Session有効期限チェック
                    if (oidcSession.expires_at && Date.now() < oidcSession.expires_at) {
                        const userData = JSON.parse(savedUser);
                        // テナントIDの検証（'default'は無効）
                        const oidcTenantId = oidcSession.tenant_id && oidcSession.tenant_id !== 'default' ? oidcSession.tenant_id : null;
                        const userTenantId = userData.tenant_id && userData.tenant_id !== 'default' ? userData.tenant_id : null;
                        const effectiveTenantId = oidcTenantId || userTenantId || validSavedTenantId;
                        // デバッグ: テナントIDの取得状況をログ出力
                        console.log('🔍 [AUTH-INIT-OIDC] Tenant ID extraction:', {
                            oidcSession_tenant_id: oidcSession.tenant_id,
                            userData_tenant_id: userData.tenant_id,
                            savedTenantId,
                            validSavedTenantId,
                            oidcTenantId,
                            userTenantId,
                            effectiveTenantId,
                            userData: {
                                id: userData.id,
                                username: userData.username,
                                email: userData.email
                            }
                        });
                        apiClient.setToken(savedToken);
                        setToken(savedToken);
                        setUser(userData);
                        setTenantId(effectiveTenantId);
                        willAuthenticate = true;
                        console.log('✅ [AUTH-INIT] OIDC session restored successfully:', {
                            user_id: oidcSession.user_info?.sub,
                            tenant_id: oidcSession.tenant_id,
                            effective_tenant_id: effectiveTenantId,
                            expires_at: new Date(oidcSession.expires_at).toISOString()
                        });
                    }
                    else {
                        if (tryRefreshExpiredCredentials('OIDC session expired')) {
                            return;
                        }
                        console.warn('⚠️ [AUTH-INIT] OIDC session expired and no refresh token is available - clearing credentials');
                        clearStoredAuthCredentials();
                    }
                }
                else if (savedToken.startsWith('demo-jwt-token-')) {
                    // デモトークンの場合は有効性チェックをスキップ（開発用）
                    console.log('🔍 [AUTH-INIT] Demo token found - development mode');
                    const userData = JSON.parse(savedUser);
                    const userTenantId = userData.tenant_id && userData.tenant_id !== 'default' ? userData.tenant_id : null;
                    const effectiveTenantId = userTenantId || validSavedTenantId;
                    apiClient.setToken(savedToken);
                    setToken(savedToken);
                    setUser(userData);
                    setTenantId(effectiveTenantId);
                    willAuthenticate = true;
                }
                else {
                    // 実際のJWTトークンの場合は有効期限をチェック
                    const tokenParts = savedToken.split('.');
                    if (tokenParts.length === 3) {
                        try {
                            let payload = tokenParts[1];
                            // Base64 URLセーフパディング追加
                            payload += '='.repeat((4 - payload.length % 4) % 4);
                            const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
                            const payloadJson = JSON.parse(decodedPayload);
                            const currentTime = Math.floor(Date.now() / 1000);
                            if (payloadJson.exp && payloadJson.exp > currentTime) {
                                // 同期的に状態を設定 + JWTからテナント情報を抽出
                                const userData = JSON.parse(savedUser);
                                // テナントIDの検証（'default'は無効）
                                const jwtTenantId = payloadJson.tenant_id && payloadJson.tenant_id !== 'default' ? payloadJson.tenant_id : null;
                                const userTenantId = userData.tenant_id && userData.tenant_id !== 'default' ? userData.tenant_id : null;
                                const effectiveTenantId = jwtTenantId || userTenantId || validSavedTenantId;
                                // デバッグ: テナントIDの取得状況をログ出力
                                console.log('🔍 [AUTH-INIT-JWT] Tenant ID extraction:', {
                                    jwt_tenant_id: payloadJson.tenant_id,
                                    userData_tenant_id: userData.tenant_id,
                                    savedTenantId,
                                    validSavedTenantId,
                                    jwtTenantId,
                                    userTenantId,
                                    effectiveTenantId,
                                    payloadJson: {
                                        ...payloadJson,
                                        // 機密情報は除外
                                        sub: payloadJson.sub,
                                        exp: payloadJson.exp,
                                        iat: payloadJson.iat
                                    },
                                    userData: {
                                        id: userData.id,
                                        username: userData.username,
                                        email: userData.email
                                    }
                                });
                                apiClient.setToken(savedToken);
                                setToken(savedToken);
                                setUser(userData);
                                setTenantId(effectiveTenantId);
                                willAuthenticate = true;
                                console.log('✅ [AUTH-INIT] JWT token restored successfully', {
                                    effective_tenant_id: effectiveTenantId
                                });
                            }
                            else {
                                // トークンが期限切れ
                                if (tryRefreshExpiredCredentials('JWT token expired')) {
                                    return;
                                }
                                console.error('JWT token expired and no refresh token is available - clearing credentials');
                                clearStoredAuthCredentials();
                            }
                        }
                        catch (jwtParseError) {
                            console.error('JWT parsing failed:', jwtParseError);
                            clearStoredAuthCredentials();
                        }
                    }
                    else {
                        // 無効なトークン形式
                        console.error('Invalid JWT token format');
                        clearStoredAuthCredentials();
                    }
                }
            }
            catch (error) {
                // トークン解析エラー
                console.error('Token parsing error:', error);
                clearStoredAuthCredentials();
            }
        }
        // 初期化完了
        setIsLoading(false);
    }, [handleOAuthSuccess, refreshToken]);
    // Token有効期限監視・自動リフレッシュ（19.2-A3）
    useEffect(() => {
        if (!token || !user)
            return;
        const savedOidcSession = localStorage.getItem('teico_oidc_session');
        if (!savedOidcSession)
            return;
        try {
            const oidcSession = JSON.parse(savedOidcSession);
            const expiresAt = oidcSession.expires_at;
            if (!expiresAt)
                return;
            // Token有効期限の5分前にリフレッシュを試行
            const refreshTime = expiresAt - (5 * 60 * 1000); // 5分前
            const timeUntilRefresh = refreshTime - Date.now();
            if (timeUntilRefresh <= 0) {
                // 即座にリフレッシュ実行
                console.log('⏰ [AUTH-REFRESH] Token near expiry - immediate refresh');
                refreshToken().then((result) => {
                    if (!result.success) {
                        console.error('❌ [AUTH-REFRESH] Automatic refresh failed:', result.error);
                        // リフレッシュ失敗時は認証状態をクリア
                        setUser(null);
                        setToken(null);
                        setTenantId(null);
                        apiClient.setToken(null);
                        clearStoredAuthCredentials();
                    }
                });
            }
            else {
                // タイマーセット
                console.log(`⏰ [AUTH-REFRESH] Auto-refresh scheduled in ${Math.round(timeUntilRefresh / 60000)} minutes`);
                const timeoutId = setTimeout(() => {
                    console.log('⏰ [AUTH-REFRESH] Automatic token refresh triggered');
                    refreshToken().then((result) => {
                        if (!result.success) {
                            console.error('❌ [AUTH-REFRESH] Scheduled refresh failed:', result.error);
                            // リフレッシュ失敗時は認証状態をクリア
                            setUser(null);
                            setToken(null);
                            setTenantId(null);
                            apiClient.setToken(null);
                            clearStoredAuthCredentials();
                        }
                    });
                }, timeUntilRefresh);
                return () => clearTimeout(timeoutId);
            }
        }
        catch (error) {
            console.error('❌ [AUTH-REFRESH] Auto-refresh setup error:', error);
        }
    }, [token, user, refreshToken]);
    // 認証成功時のみログ出力（無駄な状態変化ログを削除）
    useEffect(() => {
        if (!isLoading && user && token) {
            console.log('✅ NEW CODE: Authentication success:', {
                username: user.username,
                tenantId: tenantId,
                hasToken: !!token
            });
        }
    }, [user, token, tenantId, isLoading]);
    // トークンが変更されたときにAPIクライアントを更新
    useEffect(() => {
        if (token) {
            apiClient.setToken(token);
            setIsReady(true); // API準備完了をマーク
        }
        else {
            apiClient.setToken(null);
            setIsReady(false); // API未準備をマーク
        }
    }, [token]);
    useEffect(() => {
        apiClient.setTenantId(tenantId);
    }, [tenantId]);
    // OAuth認証開始
    const startOAuthLogin = useCallback(() => {
        console.log('🔗 [AUTH] Starting OAuth authentication flow');
        window.location.href = '/auth/oauth/authorize';
    }, []);
    const login = useCallback(async (username, password) => {
        try {
            const response = await apiClient.auth.login(username, password);
            if (response.success && response.data) {
                const { user: userData, token: accessToken } = response.data;
                // ユーザーデータの存在確認
                if (!userData) {
                    console.error('User data missing in login response:', response.data);
                    return { success: false, error: 'ユーザーデータの取得に失敗しました' };
                }
                // テナント情報を抽出
                let userTenantId = userData.tenant_id;
                // JWTからテナント情報を抽出（可能であれば）
                if (!userTenantId && accessToken) {
                    try {
                        const tokenParts = accessToken.split('.');
                        if (tokenParts.length === 3) {
                            const payload = JSON.parse(atob(tokenParts[1]));
                            userTenantId = payload.tenant_id;
                        }
                    }
                    catch (jwtError) {
                        console.warn('JWT parsing error for tenant extraction:', jwtError);
                    }
                }
                // 認証状態を設定
                // テナントIDの検証（'default'は無効）
                const effectiveTenantId = userTenantId && userTenantId !== 'default' ? userTenantId : null;
                // デバッグ: テナントIDの取得状況をログ出力
                console.log('🔍 [AUTH-LOGIN] Tenant ID extraction:', {
                    userData_tenant_id: userData.tenant_id,
                    jwt_tenant_id: userTenantId,
                    effectiveTenantId,
                    userData: {
                        id: userData.id,
                        username: userData.username,
                        email: userData.email
                    }
                });
                setUser(userData);
                setToken(accessToken);
                setTenantId(effectiveTenantId);
                apiClient.setToken(accessToken);
                // localStorage に保存
                localStorage.setItem('teico_token', accessToken);
                localStorage.setItem('teico_user', JSON.stringify(userData));
                if (effectiveTenantId) {
                    localStorage.setItem('teico_tenant_id', effectiveTenantId);
                }
                else {
                    // 'default'が保存されていた場合は削除
                    localStorage.removeItem('teico_tenant_id');
                }
                return { success: true };
            }
            else {
                console.error('Login API failed:', response.error);
                return { success: false, error: response.error || 'ログインに失敗しました' };
            }
        }
        catch (error) {
            console.error('Login network error:', error);
            return { success: false, error: 'ネットワークエラーが発生しました' };
        }
    }, []); // 空の依存関係配列でコールバックを安定化
    // MyKeys連携ログアウト機能（19.2-A4）
    const logout = useCallback(async () => {
        console.log('🔄 [AUTH-LOGOUT] Starting MyKeys SSO logout');
        try {
            const savedToken = localStorage.getItem('teico_token');
            const savedIdToken = localStorage.getItem('teico_id_token');
            const savedRefreshToken = localStorage.getItem('teico_refresh_token');
            // MyKeys SSO logout API呼び出し
            if (savedToken) {
                try {
                    const response = await fetch('/auth/oauth/logout', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        },
                        body: JSON.stringify({
                            access_token: savedToken,
                            id_token: savedIdToken,
                            refresh_token: savedRefreshToken
                        })
                    });
                    if (response.ok) {
                        console.log('✅ [AUTH-LOGOUT] MyKeys SSO logout successful');
                    }
                    else {
                        console.warn('⚠️ [AUTH-LOGOUT] MyKeys SSO logout failed, continuing local logout');
                    }
                }
                catch (ssoError) {
                    console.warn('⚠️ [AUTH-LOGOUT] MyKeys SSO logout error:', ssoError);
                }
            }
        }
        catch (error) {
            console.error('❌ [AUTH-LOGOUT] SSO logout process error:', error);
        }
        // ローカル認証状態クリア
        setUser(null);
        setToken(null);
        setTenantId(null);
        apiClient.setToken(null);
        // OIDC Token関連のlocalStorage クリア
        clearStoredAuthCredentials();
        console.log('🔄 [AUTH-LOGOUT] Complete logout - local session and MyKeys SSO cleared');
    }, []);
    // Token Revocation機能 (Phase 19.1-B3)
    const revokeToken = useCallback(async (token, tokenType) => {
        try {
            console.log('🚫 [AUTH-REVOKE] Single token revocation initiated', { tokenType });
            const response = await fetch('/auth/oauth/revoke', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    token_type_hint: tokenType
                }),
            });
            const data = await response.json();
            if (data.success) {
                console.log('✅ [AUTH-REVOKE] Token revocation successful', { tokenType });
                return { success: true };
            }
            else {
                console.warn('⚠️ [AUTH-REVOKE] Token revocation failed', data);
                return { success: false, error: data.message || 'Token revocation failed' };
            }
        }
        catch (error) {
            console.error('❌ [AUTH-REVOKE] Token revocation error:', error);
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }, []);
    const revokeAllTokens = useCallback(async () => {
        try {
            console.log('🚫🚫 [AUTH-REVOKE] All tokens revocation initiated');
            const accessToken = localStorage.getItem('teico_token') || token;
            const refreshTokenValue = localStorage.getItem('teico_refresh_token');
            const response = await fetch('/auth/oauth/revoke', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    revoke_all: true,
                    access_token: accessToken,
                    refresh_token: refreshTokenValue
                }),
            });
            const data = await response.json();
            if (data.success) {
                console.log('✅ [AUTH-REVOKE] All tokens revocation successful');
                return { success: true };
            }
            else {
                console.warn('⚠️ [AUTH-REVOKE] All tokens revocation failed', data);
                return { success: false, error: data.message || 'Token revocation failed' };
            }
        }
        catch (error) {
            console.error('❌ [AUTH-REVOKE] All tokens revocation error:', error);
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }, [token]);
    const emergencyTokenRevocation = useCallback(async () => {
        try {
            console.log('🚨 [AUTH-EMERGENCY] Emergency token revocation initiated');
            // 緊急時は全Tokenを無効化
            await revokeAllTokens();
            // 即座にローカル認証状態クリア
            setUser(null);
            setToken(null);
            setTenantId(null);
            apiClient.setToken(null);
            clearStoredAuthCredentials();
            console.log('🔒 [AUTH-EMERGENCY] Emergency revocation completed');
        }
        catch (error) {
            console.error('❌ [AUTH-EMERGENCY] Emergency revocation failed:', error);
            // エラーでもローカル状態はクリア
            clearStoredAuthCredentials();
        }
    }, [revokeAllTokens]);
    const isAuthenticated = useMemo(() => {
        return !!(user && token);
    }, [user, token]);
    // コンテキスト値を安定化 (useCallbackで安定化済みの関数は依存関係から除外)
    const value = useMemo(() => ({
        user,
        token,
        tenantId,
        login,
        startOAuthLogin,
        handleOAuthSuccess,
        refreshToken, // Token自動リフレッシュ機能（19.2-A3）
        logout,
        isAuthenticated,
        isLoading,
        isReady, // API準備完了フラグ
        // Token Revocation機能追加 (Phase 19.1-B3)
        revokeToken,
        revokeAllTokens,
        emergencyTokenRevocation,
    }), [user, token, tenantId, isAuthenticated, isLoading, isReady, login, startOAuthLogin, handleOAuthSuccess, refreshToken, logout, revokeToken, revokeAllTokens, emergencyTokenRevocation]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
